/**
 * Derive equivalent unit costs for quotation lines (display-only).
 * Aligns with server pricingBasis pack patterns (e.g. 20x500G) and weight conversions.
 */

import { ceilPrice } from './priceUtils';
import { priceLineAt } from './pricing';
import { canonicalUnit, isSameUnit } from './unitVocabulary';
import {
  LB_PER_KG,
  LITRE_PER_UNIT,
  ounceLitres,
  convertQuantity,
  convertUnitPrice,
  normalizePackStructure,
  piecesPerUnit,
  roundQuantity,
  weighsButNotOunce,
} from './packConversion';
// The ONE owner of the whole-number conversion rule (D64, F7 2026-09-25 audit)
// — `isWholeCountQuantity` (the gate) and `isFractionalWholeCount` (the red
// Quantity-cell flag built on it). Both used to be spelled out again in this
// file (`isWholeForTarget`, and this file's own `isFractionalWholeCount` using
// `Number.isInteger` — a different tolerance from `quantitiesAgree`, the one
// every other float-equality check in the conversion graph uses). Re-exported
// below so this file's own callers (the quotation grid) need no import change.
import { isWholeCountQuantity, isFractionalWholeCount, deriveRatio } from './unitConversionGraph';
import { formatQty } from './unitConversions';

export { isFractionalWholeCount };

export { LB_PER_KG };

function normUom(u: string): string {
  return String(u || '')
    .trim()
    .toUpperCase()
    .replace(/\./g, '');
}

/** e.g. "20x500G", "12 x 1 L" */
export function parsePackSizeFromDescription(desc: string): {
  count: number;
  gramsEach: number;
  totalKgPerOuter: number;
} | null {
  const text = String(desc || '').replace(/\s+/g, ' ');
  const m = text.match(
    /(\d+(?:\.\d+)?)\s*[xX*]\s*(\d+(?:\.\d+)?)\s*(G|KG|KGS|g|kg|K)\b/i
  );
  if (!m) return null;
  const count = parseFloat(m[1]);
  const size = parseFloat(m[2]);
  const u = m[3].toUpperCase();
  let gramsEach = size;
  if (u === 'KG' || u === 'K' || u === 'KGS') gramsEach = size * 1000;
  if (!Number.isFinite(count) || !Number.isFinite(gramsEach) || count <= 0 || gramsEach <= 0) {
    return null;
  }
  const totalKgPerOuter = (count * gramsEach) / 1000;
  return { count, gramsEach, totalKgPerOuter };
}

/**
 * WHICH UNITS A PACK SIZE READ OUT OF THE ITEM NAME MAY BE ATTACHED TO.
 *
 * A MIRROR, not a rule of this file's own. The owner is
 * `pricingBasis.service.js`'s `OUTER_UOMS`
 * (BUSINESS_RULES#kg-or-l-per-quoted-unit), and
 * `src/__tests__/outerUomClientServerParity.test.js` fails if these two lists
 * differ by a single member. Add a unit THERE.
 *
 * It used to be a `new Set([...])` built inline in the function below, and it
 * had drifted: it omitted CTN, PALLET, PLT, DRM and DRUM. The server fills a
 * DRUM line's Equiv. per KG / per lb columns from the item name; this file
 * re-derives every column from the LIVE unit price after somebody edits the
 * cost, and re-derived nothing — so the figure the quote was generated with
 * vanished on the first price edit, on the DRUM/CTN/PALLET spellings only,
 * while a CASE or TIN line beside it kept its number (TECH_DEBT #135).
 *
 * NOT `SINGLE_ITEM_UNITS` (BUSINESS_RULES#count-unit-one-or-group), and not
 * substitutable for it: TIN, BAG and DRUM are classified the other way there,
 * and PCS/EA/UNIT are single-item units there while being OUTER here. See the
 * server owner's comment block before touching a member.
 */
export const OUTER_UOMS = new Set([
  'TIN',
  'CASE',
  'CARTON',
  'BOX',
  'PACK',
  'BAG',
  'PCE',
  'PCS',
  'EA',
  'EACH',
  'UNIT',
  'UNITS',
  'CTN',
  'PALLET',
  'PLT',
  'DRM',
  'DRUM',
]);

/** e.g. "TOMATO KETCHUP 5KG" */
function parseSingleWeightInDescription(desc: string): number | null {
  const m = String(desc).match(/(\d+(?:\.\d+)?)\s*(KG|KGS|G)\b/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  const u = m[2].toUpperCase();
  if (!Number.isFinite(n) || n <= 0) return null;
  if (u === 'G') return n / 1000;
  return n;
}

export type AlternateQuotationPricing = {
  /** Cost per kilogram when derivable */
  perKgUsd: number | null;
  /** Cost per pound when derivable (from per-kg path) */
  perLbUsd: number | null;
  /** Cost per litre when derivable */
  perLitreUsd: number | null;
  /** Cost per smallest pack unit (e.g. one 500g stick in 20x500G) */
  perInnerUsd: number | null;
  /** Cost per outer pack the reference row is sold by (e.g. one CASE) */
  perOuterUsd: number | null;
  innerUnitLabel: string | null;
  /** How many kg one row of the line's own unit weighs, when the row says. */
  kgPerUom: number | null;
  /** How many litres one row of the line's own unit holds, when the row says. */
  litrePerUom: number | null;
  /** How many of the line's unit go into the reference row's outer pack. */
  piecesPerOuter: number | null;
  /** What that outer pack is called (CASE, BOX, CARTON...). */
  outerUomLabel: string | null;
  /** Short hint from backend basis pricing */
  basisHint: string | null;
  /** Pack size USC is quoting on (vendor-invoice / catalogue basis, e.g. "500g").
   *  There is no `requestedSize` beside it: the server stopped emitting
   *  `displayEquivalents.requestedUnitSize` on 2026-09-03, because it could only
   *  ever be filled from the vendor's pack and was published under a client
   *  label. What the client asked for is `originalRequest` on the line itself,
   *  read by `QuotationDetailPage.readOriginalRequest` (ClickUp 86d478wgu). */
  quotingSize: string | null;
};

/**
 * D170.4: the ounce is the one bridge between mass and volume, so a line asked or established in
 * L/ML/GAL never reaches KG/G/LBS/MG, and one in those never reaches a volume, unless it already
 * stands on the target's side (asked in L, established in KG, still reaches LBS).
 */
export function barredByWater(established: readonly unknown[], target: unknown): boolean {
  const side = (u: unknown) => (LITRE_PER_UNIT[canonicalUnit(u) || ''] != null ? 'volume' : weighsButNotOunce(u) ? 'mass' : null);
  const to = side(target);
  const sides = established.map(side);
  return to != null && sides.some((s) => s != null && s !== to) && !sides.includes(to);
}

/** The units a line was asked in (its RFQ's) and established in (its anchor's, else its own): `barredByWater`'s input. */
export function establishedUnits(askedUom: unknown, pricingDetails: unknown, uom: unknown): unknown[] {
  const anchor = (pricingDetails as { conversionAnchor?: { uom?: unknown } } | null | undefined)?.conversionAnchor?.uom;
  return [askedUom, typeof anchor === 'string' && anchor.trim() ? anchor : uom];
}

/**
 * Compute alternate unit prices for table columns. Primary unit price is always per current UOM.
 */
export function getAlternateQuotationPricing(
  unitPriceUsd: number,
  uom: string,
  description: string,
  pricingDetails?: Record<string, unknown> | null,
  askedUom?: string | null
): AlternateQuotationPricing {
  const out: AlternateQuotationPricing = {
    perKgUsd: null,
    perLbUsd: null,
    perLitreUsd: null,
    perInnerUsd: null,
    perOuterUsd: null,
    innerUnitLabel: null,
    kgPerUom: null,
    litrePerUom: null,
    piecesPerOuter: null,
    outerUomLabel: null,
    basisHint: null,
    quotingSize: null,
  };

  // Metadata from the server's displayEquivalents snapshot: pack sizes, labels and basis
  // notes. These describe HOW the line is quoted and stay valid when the price is edited.
  //
  // The snapshot's *numbers* deliberately are NOT used. They are written once, at generation
  // (quotationGeneration.service.js), and never refreshed on update — so after a user edited
  // the unit cost, the Equiv. per KG / per lb columns kept showing the price the quote was
  // generated at while the Unit Cost column showed the new one. Everything numeric below is
  // derived from the live `unitPriceUsd` instead, using the same rules the server applies in
  // pricingBasis.buildDisplayEquivalentsForQuotation.
  let serverAtomicPerUom: number | null = null;
  let serverKgPerUom: number | null = null;
  let serverKgPerUomSource: 'conversions' | 'pack_data' | null = null;
  let serverLitrePerUom: number | null = null;
  let serverPiecesPerOuter: number | null = null;
  let serverOuterUom: string | null = null;
  let serverPack: unknown = null;
  let ratiosStatedForUom: string | null = null;
  if (pricingDetails && typeof pricingDetails === 'object') {
    const de = pricingDetails.displayEquivalents as Record<string, unknown> | undefined;
    if (de && de.serverComputed === true) {
      const vir = pricingDetails.vendorInvoiceMatchRfq as Record<string, unknown> | undefined;
      const vih = pricingDetails.vendorInvoiceMatchHistorical as Record<string, unknown> | undefined;
      const parts: string[] = [];
      if (typeof de.notes === 'string' && de.notes.trim()) parts.push(de.notes.trim());
      if (typeof vir?.sourceFileName === 'string' && vir.sourceFileName)
        parts.push(`RFQ vendor file: ${vir.sourceFileName}`);
      if (
        typeof vih?.sourceFileName === 'string' &&
        vih.sourceFileName &&
        vih.sourceFileName !== vir?.sourceFileName
      )
        parts.push(`Historical vendor file: ${vih.sourceFileName}`);
      const uc = pricingDetails.unitConversion as Record<string, unknown> | undefined;
      if (uc && typeof uc.message === 'string') parts.push(uc.message);
      out.basisHint = parts.length ? parts.join(' · ') : null;
      if (typeof de.innerLabel === 'string' && de.innerLabel) out.innerUnitLabel = de.innerLabel;
      if (typeof de.unitSize === 'string' && de.unitSize.trim()) out.quotingSize = de.unitSize.trim();
      // Divisor for count-based lines (vendor invoice splits). The client cannot derive this
      // from the description, so it is the one numeric field worth carrying over — it is a
      // units-per-UOM ratio, not a price, so it does not go stale when the price changes.
      // `> 1`, matching the server's own gate in
      // pricingBasis.buildDisplayEquivalentsForQuotation. A unit that names one
      // item (BTL, JAR, TIN — unitVocabulary.SINGLE_ITEM_UNITS) holds exactly 1,
      // and dividing by it renders a "per derived unit" column repeating the
      // unit price sitting beside it. The server stopped emitting that column
      // for those lines with QUO-00010; this is the same rule, and the two
      // reading it differently is how a screen and a quote disagree.
      if (typeof de.rfqAtomicPerUom === 'number' && Number.isFinite(de.rfqAtomicPerUom) && de.rfqAtomicPerUom > 1) {
        serverAtomicPerUom = de.rfqAtomicPerUom;
      }
      // The physical-size ratios, for the same reason and under the same rule:
      // a ratio is a fact about the PRODUCT, not about the price, so it stays
      // true after somebody edits the cost cell. These are what let a line
      // quoted per PCS answer "and per kg?" — the client cannot derive them,
      // because the pack columns they come from live on the catalogue row, not
      // on the quotation line (`pricingBasis.dimensionRatiosForUom`).
      const num = (v: unknown): number | null =>
        typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
      serverKgPerUom = num(de.kgPerUom);
      // Where the server got that ratio. 'conversions' means it walked the
      // catalogue row's OWN stated unit conversions, which outrank the pack
      // structure below — see the guard on the packStructure branch.
      if (de.kgPerUomSource === 'conversions' || de.kgPerUomSource === 'pack_data') {
        serverKgPerUomSource = de.kgPerUomSource;
      }
      serverLitrePerUom = num(de.litrePerUom);
      serverPiecesPerOuter = num(de.piecesPerOuter);
      if (typeof de.outerUom === 'string' && de.outerUom.trim()) serverOuterUom = de.outerUom.trim();
      // The pack, stated without reference to any unit. Preferred over all four
      // ratios above, which are stated per the unit the line had AT GENERATION
      // and are never rewritten afterwards — so once this screen converts a
      // line they describe a unit it no longer has (QUO-00011).
      serverPack = de.packStructure ?? null;
      // Which unit those ratios were stated for, when the server said. Lines
      // generated before that stamp existed do not carry it, and for those the
      // ratios are still the best available answer — they are correct until the
      // line is converted, and a line that has been converted has a pack
      // structure to use instead.
      if (typeof de.uom === 'string' && de.uom.trim()) ratiosStatedForUom = de.uom.trim();
    } else {
      const uc = pricingDetails.unitConversion as Record<string, unknown> | undefined;
      if (uc && typeof uc.message === 'string') {
        out.basisHint = uc.message;
      }
      if (uc?.basis === 'atomic_count' && typeof uc.message === 'string') {
        out.basisHint = uc.message;
      }
    }
  }

  if (!Number.isFinite(unitPriceUsd) || unitPriceUsd <= 0) {
    return out;
  }

  const u = normUom(uom);
  const desc = String(description || '');

  // The server's ratios first. Everything below this block recovers a pack size
  // by pattern-matching the item NAME, which only works when a vendor happened
  // to print one there — "ironman" does not, and a row stating in two typed
  // columns that a piece weighs 1 kg and ten go in a case still reported
  // nothing, so the Unit dropdown refused every conversion. Prices are derived
  // from the LIVE `unitPriceUsd`, never from a stored price.
  // The pack structure answers everything below and answers it for whatever unit
  // the line is quoted in RIGHT NOW, so when it is present it is the only source
  // consulted — mixing it with the per-unit ratios is how a figure derived from
  // the current unit ends up beside one derived from the unit at generation.
  const packStruct = serverPack ? normalizePackStructure(serverPack) : null;
  // Compared on the CANONICAL spelling, so a line whose unit is stored as
  // "CASES" is not offered a conversion to "CASE" it is already in.
  const uCanon = canonicalUnit(u);
  // D170.4: asked or established in a volume, no weight figure; in KG/G/LBS/MG, no litre figure.
  const established = establishedUnits(askedUom, pricingDetails, u);
  const weightBarred = barredByWater(established, 'KG');
  const litresBarred = barredByWater(established, 'L');
  const piecesOfCurrent = packStruct ? piecesPerUnit(u, packStruct) : null;
  if (packStruct && piecesOfCurrent != null && piecesOfCurrent > 0) {
    const per = (target: string): number | null =>
      convertUnitPrice({ price: unitPriceUsd, fromUom: u, toUom: target, pack: packStruct });

    // The one exception to "the pack structure answers everything below".
    //
    // When the server derived its KG ratio by walking the catalogue row's OWN
    // stated unit conversions, that is the same owner the item's price levels
    // and the `graph_conversion` repricing branch both read, and it outranks a
    // pack size recovered from the matched vendor invoice.
    //
    // QUO-00013 is why this guard exists (ClickUp 86d46vbf5): the row stated
    // 1 CASE = 200 KG and priced KG at TT$0.50, while this branch published
    // 10 pieces x 13.61 KG = 136.1 KG a case and US$0.12/KG — TT$0.78, against
    // a catalogue page reading TT$0.50, on the customer-facing figure.
    //
    // Only the KG/LB pair defers. Everything else here — inner, outer, litre —
    // is genuinely the pack structure's to answer and is untouched.
    //
    // That KG figure is stated per `de.uom` — the unit at generation — so it is
    // carried through the pack to the unit the line is in NOW. Read raw, a PCS
    // line converted to KG divided its KG price by a can's weight again and
    // showed US$342.15 a kilo beside US$48.38 (QA, QUO-00171).
    const statedPieces = ratiosStatedForUom ? piecesPerUnit(ratiosStatedForUom, packStruct) : piecesOfCurrent;
    const kgPerCurrentFromConversions =
      !weightBarred && serverKgPerUomSource === 'conversions' && serverKgPerUom != null && statedPieces != null && statedPieces > 0
        ? (serverKgPerUom * piecesOfCurrent) / statedPieces
        : null;

    if (kgPerCurrentFromConversions != null) {
      out.kgPerUom = kgPerCurrentFromConversions;
      out.perKgUsd = ceilPrice(unitPriceUsd / kgPerCurrentFromConversions);
      out.perLbUsd = ceilPrice((unitPriceUsd / kgPerCurrentFromConversions) * LB_PER_KG);
    } else if (!weightBarred && packStruct.kgPerPiece != null) {
      out.kgPerUom = packStruct.kgPerPiece * piecesOfCurrent;
      const kg = per('KG');
      const lb = per('LBS');
      if (kg != null) out.perKgUsd = ceilPrice(kg);
      if (lb != null) out.perLbUsd = ceilPrice(lb);
    }
    // A stated volume, or a stated ounce at the item's litres per ounce (D170.4); `per` refuses L from a KG line.
    const anchorOrOwn = canonicalUnit(established[1]) || '';
    const litrePerPiece = packStruct.litrePerPiece ?? (packStruct.ozPerPiece != null ? packStruct.ozPerPiece * ounceLitres(packStruct) : null);
    const l = litrePerPiece != null && !litresBarred ? per('L') : null;
    if (litrePerPiece != null && l != null) {
      out.litrePerUom = litrePerPiece * piecesOfCurrent;
      out.perLitreUsd = ceilPrice(l);
    } else if (uCanon === 'OZ' && !litresBarred && (anchorOrOwn === 'OZ' || LITRE_PER_UNIT[anchorOrOwn] != null)) {
      // No piece states litres: the line's own ounce fills them (the item's litres per ounce, else
      // by water), when it was established in ounces or a volume. From a piece of grams, no volume (D170.4).
      out.litrePerUom = ounceLitres(packStruct);
      out.perLitreUsd = ceilPrice(unitPriceUsd / out.litrePerUom);
    }

    // The way back to a single piece. Its absence is why PCS -> CASE -> PCS had
    // no return path and the dropdown refused the unit the line started in.
    // D104.11: an INNER is a piece no bigger than the line's own unit. A KG line whose piece is a
    // 17.28 KG case holds a fraction of a piece, and its "per piece" is a whole-case price, not an
    // inner (US$393.47 beside US$22.77/KG). Such a line has no inner: blank, never a case price.
    if (uCanon !== 'PCS' && piecesOfCurrent >= 1) {
      const inner = per('PCS');
      if (inner != null) {
        out.perInnerUsd = ceilPrice(inner);
        out.innerUnitLabel = out.innerUnitLabel || 'PCS';
      }
    }

    // The outer pack, offered only to a line that is not already quoted in it.
    // A CASE line has no bigger pack on file, and offering one at unitPrice x
    // piecesPerOuter is a case of cases at ten times the money.
    if (packStruct.outerUom && packStruct.piecesPerOuter != null && packStruct.outerUom !== uCanon) {
      const outer = per(packStruct.outerUom);
      if (outer != null) {
        out.perOuterUsd = ceilPrice(outer);
        out.outerUomLabel = packStruct.outerUom;
        out.piecesPerOuter = packStruct.piecesPerOuter / piecesOfCurrent;
      }
    }

    return out;
  }

  // No pack structure. The per-unit ratios are all that is on file, and they are
  // only safe on a line still quoted in the unit they were stated for.
  const ratiosApply = ratiosStatedForUom == null || normUom(ratiosStatedForUom) === u;

  if (serverKgPerUom != null && ratiosApply && !weightBarred) {
    out.kgPerUom = serverKgPerUom;
    out.perKgUsd = ceilPrice(unitPriceUsd / serverKgPerUom);
    out.perLbUsd = ceilPrice((unitPriceUsd / serverKgPerUom) * LB_PER_KG);
  }
  if (serverLitrePerUom != null && ratiosApply && !litresBarred) {
    out.litrePerUom = serverLitrePerUom;
    out.perLitreUsd = ceilPrice(unitPriceUsd / serverLitrePerUom);
  }
  // Also gated on the line not already BEING the outer pack: the count was
  // stated per the generation unit, and read against a line since converted
  // into that same outer it offers a case containing ten cases.
  if (
    serverPiecesPerOuter != null &&
    ratiosApply &&
    (!serverOuterUom || normUom(serverOuterUom) !== u)
  ) {
    out.piecesPerOuter = serverPiecesPerOuter;
    out.outerUomLabel = serverOuterUom;
    out.perOuterUsd = ceilPrice(unitPriceUsd * serverPiecesPerOuter);
  }

  // Line is already priced per kg
  if (u === 'KGS' || u === 'KG' || u === 'KILO' || u === 'KILOS') {
    out.kgPerUom = 1;
    out.perKgUsd = ceilPrice(unitPriceUsd);
    out.perLbUsd = ceilPrice(unitPriceUsd * LB_PER_KG);
    return out;
  }

  // Line is priced per lb / per gram. The server's weight branch covers every weight UOM
  // (pricingBasis.getUnitCategory), so these need handling here too — otherwise deriving the
  // equivalents locally would blank the columns on lines the server used to fill in.
  if (u === 'LB' || u === 'LBS' || u === 'POUND' || u === 'POUNDS') {
    out.kgPerUom = LB_PER_KG;
    out.perLbUsd = ceilPrice(unitPriceUsd);
    out.perKgUsd = ceilPrice(unitPriceUsd / LB_PER_KG);
    return out;
  }
  if (u === 'G' || u === 'GRAM' || u === 'GRAMS' || u === 'GM' || u === 'GMS') {
    out.kgPerUom = 0.001;
    out.perKgUsd = ceilPrice(unitPriceUsd * 1000);
    out.perLbUsd = ceilPrice(unitPriceUsd * 1000 * LB_PER_KG);
    return out;
  }

  // Line is priced per a VOLUME unit — the litre analog of the three weight
  // branches above, and of the server's `dimensionRatiosForUom`, which answers a
  // GAL line in litres too. Read through `LITRE_PER_UNIT` rather than a set of
  // spellings, because that is where a gallon is four litres (the owner's,
  // 2026-09-14). Without it a GAL line added by hand carried no ratio at all, so
  // the Unit dropdown could not restate it in litres. Litres only: a volume is
  // never a kilogram.
  const litresPerUom = LITRE_PER_UNIT[canonicalUnit(u) || ''];
  if (litresPerUom != null) {
    out.litrePerUom = litresPerUom;
    out.perLitreUsd = ceilPrice(unitPriceUsd / litresPerUom);
    return out;
  }

  // Gated on the server having said nothing, mirroring the same precedence in
  // pricingBasis.buildDisplayEquivalentsForQuotation: a figure a person typed
  // into the catalogue's pack columns outranks one pattern-matched out of the
  // item name. The two agree on most lines — the size cell is usually
  // transcribed from that same text — and where they disagree, correcting the
  // row has to be what wins.
  const pack = out.kgPerUom == null ? parsePackSizeFromDescription(desc) : null;

  if (pack && pack.totalKgPerOuter > 0 && OUTER_UOMS.has(u)) {
    out.kgPerUom = pack.totalKgPerOuter;
    out.perKgUsd = ceilPrice(unitPriceUsd / pack.totalKgPerOuter);
    out.perLbUsd = ceilPrice((unitPriceUsd / pack.totalKgPerOuter) * LB_PER_KG);
    out.perInnerUsd = ceilPrice(unitPriceUsd / pack.count);
    out.innerUnitLabel =
      out.innerUnitLabel ||
      (pack.gramsEach >= 1000
        ? `${(pack.gramsEach / 1000).toFixed(2)}kg unit`
        : `${pack.gramsEach}g unit`);
    out.quotingSize =
      out.quotingSize ||
      (pack.gramsEach >= 1000 ? `${(pack.gramsEach / 1000).toFixed(2)}kg` : `${pack.gramsEach}g`);
    return out;
  }

  const kgFromDesc = out.kgPerUom == null ? parseSingleWeightInDescription(desc) : null;
  if (kgFromDesc != null && kgFromDesc > 0 && OUTER_UOMS.has(u)) {
    out.kgPerUom = kgFromDesc;
    out.perKgUsd = ceilPrice(unitPriceUsd / kgFromDesc);
    out.perLbUsd = ceilPrice((unitPriceUsd / kgFromDesc) * LB_PER_KG);
    return out;
  }

  // Count-based line (e.g. a case of N atomic units per the vendor invoice split).
  if (serverAtomicPerUom != null) {
    out.perInnerUsd = ceilPrice(unitPriceUsd / serverAtomicPerUom);
    out.innerUnitLabel = out.innerUnitLabel || 'per derived unit';
  }

  return out;
}

// ───────────────────────────────── THE UNIT CONTROL'S TWO DECISIONS ──────────
//
// Both lived inline in `QuotationDetailPage.handleLineItemUnitChange` and
// `updateQuotationItem`, where nothing could test them — the page is a god file
// with no test of its own, so a rule that costs money was pinned only by the
// comment beside it. Extracted here, beside the other helpers this screen's
// unit control already reads from, with the page left calling them (CLAUDE.md:
// strangle, do not rewrite — take the slice the task touches and delegate).
//
// Pure, both of them. No React, no I/O.

/** What a conversion restates a line FROM, in the order it is asked. */
export type QuantitySource = 'rfq' | 'anchor' | 'line' | 'priceRatio' | 'unchanged' | 'refused';

export type RestateQuantityArgs = {
  /** What the vessel asked for — `originalRequest`, the durable record. */
  rfqAsked?: { quantity: number | null; uom: string | null } | null;
  /** The last point the line was ESTABLISHED at. */
  anchor?: { quantity?: number | null; uom?: string | null } | null;
  currentQty: number;
  currentUom: string;
  currentUnitPrice: number;
  targetUnitPrice: number;
  targetUnit: string;
  pack: unknown;
  /**
   * The picked catalogue row's OWN stated unit conversions ("1 PACK = 2 CASE",
   * "1 PCS = 500 ML") — tried by `restateQuantityThroughPack` ONLY when `pack`
   * cannot express the hop (F3, 2026-09-25): `packStructureOfCatalogueRow`
   * knows a two-level pack and cannot state "1 PACK = 2 CASE" at all, so a row
   * that already PRICES every unit its conversions reach (`priceLevels.byUnit`,
   * the lookup panel) fell through to `no_data` here with a false "does not
   * say how many X that is". Catalogue-pick restatement only — the Unit
   * dropdown restates within the SAME product and a quotation line carries no
   * conversion list, so it never sets this. Null still reaches the fixed
   * measure stars (KG/LBS, L/ML/GAL, MTR), so a measure needs no list.
   */
  conversions?: unknown;
};

/**
 * How many of the target unit this line becomes, and on what authority.
 *
 * FOUR sources, best first, and the first is the RFQ itself — owner
 * instruction on ClickUp 86d478wv2: "the conversion should ALWAYS use that as
 * the base". `originalRequest` is written once at generation and rewritten by
 * nothing (TECH_DEBT #151), so it is the only source here that cannot drift.
 *
 * THE CONSEQUENCE IS DELIBERATE AND WAS DECIDED, not overlooked: source 1
 * outranks a reviewer's own corrected count, so correcting 2 CASE to 3 and then
 * touching the Unit dropdown restates back to the RFQ's ask. The concern was
 * raised before this shipped and answered "the RFQ always wins".
 *
 * `source` is returned as well as the number so a caller — or a test — can say
 * WHICH answer it got, rather than inferring it from an arithmetic coincidence.
 */
export function restateQuantity(args: RestateQuantityArgs): {
  quantity: number;
  source: QuantitySource;
  /** Set only when `source` is 'refused' — ready for an in-app notice. */
  message?: string;
} {
  const { currentQty } = args;

  const throughPack = restateQuantityThroughPack(args);
  if (throughPack.ok) return { quantity: throughPack.quantity, source: throughPack.source };

  // WHOLE-NUMBER CONVERSION RULE (owner, 2026-09-24): the pack states the
  // exact ratio, and it does not divide evenly into the target's countable
  // unit — "if a case has 150 kg then we shouldn't allow converting cause it
  // doesn't make sense." The price-ratio fallback below is an APPROXIMATION
  // (it works off rounded prices, not a stated pack ratio) and cannot promise
  // a shippable whole count, so it is not tried here. No rounding up: the
  // line stays exactly as it was and the caller is told why.
  if (throughPack.reason === 'not_whole') {
    return { quantity: currentQty, source: 'refused', message: throughPack.message };
  }

  // No pack at all. Both ratios are pieces-per-target over pieces-per-current,
  // so this restates the amount as faithfully as the data allows and keeps the
  // line total intact. Rounded prices make it approximate; leaving the quantity
  // alone makes it wrong by the whole ratio, which is the bug this replaced.
  const { currentUnitPrice, targetUnitPrice, currentUom, targetUnit } = args;
  const fromRatio =
    currentUnitPrice > 0 && targetUnitPrice > 0
      ? roundQuantity(currentQty * (currentUnitPrice / targetUnitPrice))
      : null;
  if (fromRatio != null) {
    // OWNER DECISION A, F4 (2026-09-25): a price ratio is still a restatement,
    // and "if a case has 150 kg then we shouldn't allow converting" applies to
    // it exactly as it does to a stated pack ratio — REFUSE when the target
    // counts things and the ratio does not land on a whole number, by the
    // SAME gate the pack path above uses. This is narrower than it looks: a
    // WHOLE result via the price ratio, and any CONTINUOUS target, keep
    // today's behaviour untouched (this file's pinned "restates by the price
    // ratio, never by leaving it alone" test still passes at 2 CASE — it was
    // never the fractional case). Only a fractional count of a whole-count
    // unit is new here.
    // ...and a count restated into a measure lands whole too (QUO-00171, above).
    if (!isWholeCountQuantity(fromRatio, targetUnit) || !isWholeCountQuantity(fromRatio, currentUom)) {
      const to = canonicalUnit(targetUnit) || String(targetUnit).toUpperCase();
      const from = canonicalUnit(currentUom) || String(currentUom).toUpperCase();
      return {
        quantity: currentQty,
        source: 'refused',
        message:
          `${formatQty(currentQty)} ${from} is ${formatQty(fromRatio)} ${to} (by price ratio, no stated pack), ` +
          `not a whole number — quoted per ${from} instead`,
      };
    }
    return { quantity: fromRatio, source: 'priceRatio' };
  }

  return { quantity: currentQty, source: 'unchanged' };
}

/** A restatement the pack actually supports, whole and ready to write. */
export type PackRestatementSuccess = { ok: true; quantity: number; source: 'rfq' | 'anchor' | 'line' };

/**
 * Why a pack-based restatement was refused.
 *
 *   'no_data'    — nothing states a path from any of the three sources to the
 *                  target unit at all (no pack, or the wrong dimension).
 *   'not_whole'  — a path exists and was walked, and what it computes is not a
 *                  whole number of a target unit that COUNTS things. `source`,
 *                  `quantity` and `fromUnit` are the record that produced the
 *                  refusal (the RFQ's own ask, when it has one), and
 *                  `computedQuantity` is the honest fractional answer nobody
 *                  ships — named so a caller building its own message never
 *                  has to re-derive it.
 */
export type PackRestatementRefusal =
  | { ok: false; reason: 'no_data' }
  | {
      ok: false;
      reason: 'not_whole';
      source: 'rfq' | 'anchor' | 'line';
      quantity: number;
      fromUnit: string;
      targetUnit: string;
      computedQuantity: number;
      /** Ready to show as an in-app notice, in the owner's own words. */
      message: string;
    };

export type PackRestatement = PackRestatementSuccess | PackRestatementRefusal;

/**
 * `restateQuantity`'s first three sources — the RFQ, the anchor, the line —
 * and nothing after them: a refusal where none of the three can be restated.
 *
 * Split out for the catalogue search pick (QUO-00054), which must NOT fall
 * through to the price ratio. On the Unit dropdown both prices describe the
 * SAME product, so their ratio is its pack ratio. A pick swaps the product: TT$4
 * a case of the old row over TT$2 a litre of the new one is no fact about
 * either, and "leave it alone" is how 20 CASE of water became 20 L. A pick that
 * cannot restate the amount is refused by its caller instead.
 *
 * A unit restated in ITSELF needs no pack: 20 CASE asked for is 20 CASE. This
 * went through the pack, and a line with none fell to the price ratio and then
 * "unchanged" — so the Unit dropdown could not take QUO-00054's line back from
 * its wrong 20 L to the 20 CASE the vessel asked for; it offered 1.7 CASE.
 *
 * The FIRST source with an actual quantity+unit stated is the one this
 * commits to — success or refusal. It does not fall through to a lower-
 * priority source just because that one happens to divide evenly: the RFQ
 * always wins (ClickUp 86d478wv2), and a stale anchor that coincidentally
 * lands on a whole case is not evidence of what was actually asked.
 */
export function restateQuantityThroughPack(
  args: Omit<RestateQuantityArgs, 'currentUnitPrice' | 'targetUnitPrice'>
): PackRestatement {
  const { rfqAsked, anchor, currentQty, currentUom, targetUnit, pack, conversions } = args;

  const attempts: Array<{ quantity: unknown; fromUom: unknown; source: 'rfq' | 'anchor' | 'line' }> = [
    { quantity: rfqAsked?.quantity, fromUom: rfqAsked?.uom, source: 'rfq' },
    { quantity: anchor?.quantity, fromUom: anchor?.uom, source: 'anchor' },
    { quantity: currentQty, fromUom: currentUom, source: 'line' },
  ];

  // How many `to` in one `from`, tried through whatever this row actually
  // states — the pack first (`packStructureOfCatalogueRow`'s two-level
  // structure), then the row's OWN conversion graph (F3, 2026-09-25 audit)
  // when the pack cannot express the hop at all. "1 PACK = 2 CASE",
  // "1 CASE = 12 PCS", "1 PCS = 500 ML" is exactly a shape no two-level pack
  // can state, and this row already PRICES every one of those units in the
  // lookup panel off this same graph (`priceLevels.byUnit`,
  // `catalogueCostUnitOptions`) — `deriveRatio` is that existing owner, not a
  // new walk, so the quantity a pick restates to and the price it is offered
  // at now come from the same place. An ounce crosses at the pack's litres per
  // ounce, the figure `targetUnitPriceFor` prices by (D170.4).
  const ounce = { litrePerOunce: normalizePackStructure(pack).litrePerOunce };
  const oneUnitAs = (from: unknown, to: unknown): number | null => {
    if (pack) {
      const viaPack = convertQuantity({ quantity: 1, fromUom: from, toUom: to, pack });
      if (viaPack != null) return viaPack;
    }
    return deriveRatio(conversions ?? null, from, to, ounce);
  };

  for (const { quantity, fromUom, source } of attempts) {
    if (quantity == null || !(Number(quantity) > 0) || !fromUom) continue;

    if (isSameUnit(fromUom, targetUnit)) {
      return { ok: true, quantity: roundQuantity(quantity) as number, source };
    }
    // No skip for a row with neither pack nor graph: `deriveRatio` seeds the
    // fixed measure stars (KG/G/LBS/OZ, L/ML/GAL, MTR) for a null list, so
    // 25 KG -> LBS is 55.25 (D60) instead of falling to the price ratio.

    let converted = pack ? convertQuantity({ quantity, fromUom, toUom: targetUnit, pack }) : null;
    if (converted == null) {
      const ratio = deriveRatio(conversions ?? null, fromUom, targetUnit, ounce);
      if (ratio != null) converted = Number(quantity) * ratio;
    }
    if (converted == null) continue;
    const rounded = roundQuantity(converted) as number;
    const from = canonicalUnit(String(fromUom)) || String(fromUom).toUpperCase();
    const to = canonicalUnit(String(targetUnit)) || String(targetUnit).toUpperCase();

    // A COUNT restated into a measure must land whole too (QA, QUO-00171):
    // 20 PCS of a 5 oz can went to "2.828054 KG" — KG is continuous, so the
    // target alone let it through, but nobody quotes a vessel 2.828054 kg of
    // cans. A measure asked in a measure (100 KG -> 220.46 LB) is untouched.
    const targetWhole = isWholeCountQuantity(rounded, targetUnit);
    if (!targetWhole || !isWholeCountQuantity(rounded, fromUom)) {
      // How many `from` one `to` actually is, through whichever path produced
      // the conversion above — named in the message so it reads as the fact
      // that refused the conversion ("one CASE is 150 KG"), not just the
      // fraction it would have produced.
      const perTargetUnit = oneUnitAs(targetUnit, fromUom);
      return {
        ok: false,
        reason: 'not_whole',
        source,
        quantity: Number(quantity),
        fromUnit: from,
        targetUnit: to,
        computedQuantity: rounded,
        message: targetWhole
          ? `${formatQty(Number(quantity))} ${from} is ${formatQty(rounded)} ${to}, not a whole number — quoted per ${from} instead`
          : perTargetUnit != null
            ? `One ${to} is ${formatQty(perTargetUnit)} ${from}, so ${formatQty(Number(quantity))} ${from} is not a whole number of ${to} — quoted per ${from} instead`
            : `${formatQty(Number(quantity))} ${from} is not a whole number of ${to} — quoted per ${from} instead`,
      };
    }
    return { ok: true, quantity: rounded, source };
  }

  return { ok: false, reason: 'no_data' };
}

/**
 * What an edit to one cell does to the line's conversion provenance.
 *
 * A hand-edited PRICE is the user stating a new truth for the unit the line is
 * in right now, which retires the fixed point entirely: keeping it would
 * restate their edit away the moment they touched the Unit dropdown.
 *
 * A QUANTITY edit is NOT the same statement, and treating it as one is how the
 * US$ cost drifted (ClickUp 86d478wv2). Correcting 2 CASE to 3 CASE says
 * nothing whatever about what a CASE costs — so the count the anchor remembers
 * goes, and the PRICE it remembers must not. Dropping the whole anchor
 * discarded `anchor.unitPrice`, the last unrounded price anybody established;
 * `getTargetUnitPrice` then fell back to the line's own `ceilPrice`d figure and
 * the next conversion ratcheted:
 *
 *     ceilPrice(15.39 / 10) = 1.54     ceilPrice(1.54 * 10) = 15.40
 *
 * which is how QUO-00013's two lines came out a cent apart off one catalogue
 * row. The count is retired as `quantity: null` rather than by deleting it, so
 * the block still READS as an anchor to `readConversionAnchor` — which requires
 * a uom and a price and treats a missing count as "no count on file", the state
 * every anchor written before that field existed is already in.
 *
 * `partPackRounding` goes either way: it says "2.5 was rounded to 3", and once
 * somebody types a quantity of their own it describes a row that no longer
 * exists. A stale badge claiming a decision nobody made is worse than no badge.
 *
 * Returns the details object to store. The input is never mutated, and an
 * object with nothing to drop is returned unchanged so a caller can skip a
 * write.
 */
export function pricingDetailsAfterFieldEdit<T extends Record<string, unknown>>(
  pricingDetails: T | null | undefined,
  field: string
): T | null | undefined {
  const dropsWholeAnchor =
    field === 'unitPrice' || field === 'unitSellingPrice' || field === 'competitorPrice';
  if (!dropsWholeAnchor && field !== 'quantity') return pricingDetails;

  const pd = pricingDetails;
  if (!pd || typeof pd !== 'object') return pricingDetails;
  if (!pd.conversionAnchor && !pd.partPackRounding) return pricingDetails;

  const { conversionAnchor: anchorWas, partPackRounding: _dropped, ...rest } = pd;

  const anchor = anchorWas as Record<string, unknown> | null | undefined;
  if (dropsWholeAnchor || !anchor || typeof anchor !== 'object') return rest as T;

  return { ...rest, conversionAnchor: { ...anchor, quantity: null } } as unknown as T;
}

/**
 * The selling price a unit conversion leaves on a line (D85) — the ONE owner of
 * which of three answers applies.
 *
 * A line that is NOT hand-priced (`quotedBefore.source !== 'derived'`) is quoted
 * at a RULE (a markup percentage), so the conversion re-applies that rule to the
 * restated cost, marked up from its EXACT (unrounded) figure: TT$2.00 PCS -> CASE of
 * 12 is US$4.0008 x 1.30 = 5.2010 -> 5.21. Scaling its stored (already ceiled) selling price instead
 * gives ceil(0.44 x 12) = 5.28, and every hop ratchets. Generated lines STORE a
 * selling price now, so "has a stored price" no longer means "hand-priced".
 *
 * Only while the line IS at its rule (`atStatedRate`, `isPricedAtStatedRate`
 * asked before the hop). A line whose cost moved after it was priced (the
 * QUO-00013 shape the stale-markup tooltip names) carries money its rate no
 * longer produces; a unit change restates that money, it does not quietly
 * re-price it.
 *
 * A hand-priced or stale line has no rule to re-apply: it restates through the
 * conversion anchor's own selling price when there is one, else scales its price
 * by the cost ratio (the long-standing behaviour). Only the re-priced answer
 * carries `markup` — a hand-priced line keeps its null.
 */
export function restateSellingPrice(args: {
  quotedBefore: { source: string; markupPercent: number };
  /** `isPricedAtStatedRate` of the line BEFORE the hop. */
  atStatedRate: boolean;
  /** Selling price of the line before the hop. */
  currentUnitSelling: number | null | undefined;
  /** Cost of the line before the hop. */
  currentUnitPrice: number;
  /** The anchor's selling price restated into the target unit, or null. */
  anchoredSelling: number | null;
  targetUnitPrice: number;
  convertedQty: number;
  /** Rate for the rare hand-priced line with nothing to restate from; defaults to the line's own. */
  fallbackMarkupPercent?: number;
}): { unitSellingPrice: number; totalSellingPrice: number; markup?: number | null } {
  const { quotedBefore, currentUnitSelling, currentUnitPrice, anchoredSelling, targetUnitPrice, convertedQty } = args;
  // The stored cost is the ceiled `targetUnitPrice`; the markup goes on the unrounded one (D85).
  if (quotedBefore.source !== 'derived' && args.atStatedRate) {
    return priceLineAt({
      unitCost: ceilPrice(targetUnitPrice),
      unitCostExact: targetUnitPrice,
      quantity: convertedQty,
      markupPercent: quotedBefore.markupPercent,
    });
  }
  if (anchoredSelling != null) {
    return { unitSellingPrice: anchoredSelling, totalSellingPrice: ceilPrice(convertedQty * anchoredSelling) };
  }
  if (currentUnitSelling && currentUnitSelling > 0 && currentUnitPrice > 0) {
    // The ratio of the two STORED (ceiled) costs, as before D85: a hand-priced or
    // stale line's money is restated exactly as it always was, cent for cent.
    const unitSellingPrice = ceilPrice(currentUnitSelling * (ceilPrice(targetUnitPrice) / currentUnitPrice));
    return { unitSellingPrice, totalSellingPrice: ceilPrice(convertedQty * unitSellingPrice) };
  }
  return priceLineAt({
    unitCost: ceilPrice(targetUnitPrice),
    unitCostExact: targetUnitPrice,
    quantity: convertedQty,
    markupPercent: args.fallbackMarkupPercent ?? quotedBefore.markupPercent,
  });
}

// The unit families a Unit change treats as the same unit (the target-price reader is `unitChangeTarget`).
export const KG_UNITS = new Set(['KG', 'KGS', 'KILO', 'KILOS']);
export const LB_UNITS = new Set(['LB', 'LBS', 'POUND', 'POUNDS']);

export const isEquivalentUnitFamily = (a: string, b: string): boolean => {
  if (a === b) return true;
  if (KG_UNITS.has(a) && KG_UNITS.has(b)) return true;
  if (LB_UNITS.has(a) && LB_UNITS.has(b)) return true;
  return false;
};
